import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Upload, Search, FileText, Download, Trash2, Eye, Calendar, Building2, AlertTriangle, FileDown, ExternalLink, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [uploads, setUploads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState("ask"); // "ask", "add", "skip"
  
  // Pagination states
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceDates, setInvoiceDates] = useState([]);
  const [selectedInvoiceDate, setSelectedInvoiceDate] = useState("");
  const [weekNumbers, setWeekNumbers] = useState([]);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState("");
  const [warningFilter, setWarningFilter] = useState(""); // "", "true", "false"
  const [zeroFixedFilter, setZeroFixedFilter] = useState(""); // "", "true", "false"
  
  const navigate = useNavigate();

  const fetchUploads = async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (searchTerm) params.search = searchTerm;
      if (selectedCompany) params.company = selectedCompany;
      if (selectedInvoiceDate) params.invoice_date = selectedInvoiceDate;
      if (selectedWeekNumber) params.week_number = selectedWeekNumber;
      if (warningFilter) params.has_warning = warningFilter === "true";
      if (zeroFixedFilter) params.has_zero_fixed = zeroFixedFilter === "true";
      if (dateFilter) {
        params.date_filter = dateFilter;
        if (dateFilter === 'custom' && startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }
      }
      
      const response = await axios.get(`${API}/uploads`, { params });
      // Backend now returns {uploads: [], pagination: {}}
      setUploads(response.data.uploads || []);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching uploads:", error);
      toast.error("Yüklemeler alınamadı");
    }
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  }, []);

  const fetchInvoiceDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invoice-dates`);
      setInvoiceDates(response.data.invoice_dates || []);
    } catch (error) {
      console.error("Error fetching invoice dates:", error);
    }
  }, []);

  const fetchWeekNumbers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/week-numbers`);
      setWeekNumbers(response.data.week_numbers || []);
    } catch (error) {
      console.error("Error fetching week numbers:", error);
    }
  }, []);

  useEffect(() => {
    fetchUploads();
  }, [searchTerm, selectedCompany, pagination.page, pagination.limit, dateFilter, startDate, endDate, selectedInvoiceDate, selectedWeekNumber, warningFilter, zeroFixedFilter]);
  
  useEffect(() => {
    fetchCompanies();
    fetchInvoiceDates();
    fetchWeekNumbers();
  }, []);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const pdfFiles = Array.from(files).filter(file => file.name.endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error("Lütfen geçerli PDF dosyaları seçin");
      return;
    }

    setUploading(true);
    const progressArray = pdfFiles.map((file, idx) => ({
      id: idx,
      filename: file.name,
      status: 'uploading',
      progress: 0
    }));
    setUploadProgress(progressArray);

    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      // duplicateMode === "add" ise force=true gönder (otomatik ekle)
      // duplicateMode === "skip" ise force=false ve duplicate gelirse atla
      // duplicateMode === "ask" ise force=false ve duplicate gelirse sor
      await uploadSingleFile(file, i, duplicateMode === "add");
    }
    
    const completedCount = uploadProgress.filter(p => p.status === 'completed').length;
    if (completedCount > 0) {
      toast.success(`${completedCount} PDF başarıyla yüklendi!`);
    }
    
    setTimeout(() => {
      setUploading(false);
      setUploadProgress([]);
      fetchUploads();
      fetchCompanies(); // Refresh company list after upload
    }, 1500);
  };

  const uploadSingleFile = async (file, index, force = false) => {
    const formData = new FormData();
    formData.append("file", file);
    if (force) {
      formData.append("force", "true");
    }

    try {
      const response = await axios.post(`${API}/upload-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Check for duplicate
      if (response.data.duplicate) {
        if (duplicateMode === "skip") {
          // Otomatik atla - sormadan geç
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === index ? {...p, status: 'skipped', progress: 0} : p
          ));
          return;
        } else if (duplicateMode === "ask") {
          // Sor - alert göster
          const confirmed = window.confirm(
            `⚠️ Bu fatura zaten eklenmiş!\n\n` +
            `Fatura No: ${response.data.invoice_number}\n` +
            `Şirket: ${response.data.existing_company}\n` +
            `Mevcut Dosya: ${response.data.existing_file}\n\n` +
            `Yine de eklemek istiyor musunuz?`
          );
          
          if (confirmed) {
            // Retry with force=true
            await uploadSingleFile(file, index, true);
          } else {
            setUploadProgress(prev => prev.map((p, idx) => 
              idx === index ? {...p, status: 'skipped', progress: 0} : p
            ));
          }
          return;
        }
        // duplicateMode === "add" durumunda buraya gelmez çünkü force=true gönderilir
      }
      
      setUploadProgress(prev => prev.map((p, idx) => 
        idx === index ? {...p, status: 'completed', progress: 100} : p
      ));
      
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      toast.error(error.response?.data?.detail || "PDF yükleme hatası");
      
      setUploadProgress(prev => prev.map((p, idx) => 
        idx === index ? {...p, status: 'failed', progress: 0} : p
      ));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/uploads/${id}`);
      toast.success("Kayıt silindi");
      fetchUploads();
    } catch (error) {
      console.error("Error deleting upload:", error);
      toast.error("Silme hatası");
    }
  };

  const handleSendToServer = async (id, e) => {
    e.stopPropagation();
    
    // Check if upload has validation warning
    const upload = uploads.find(u => u.id === id);
    if (upload && upload.has_validation_warning) {
      // Show confirmation dialog for warning PDFs
      const confirmSend = window.confirm(
        `⚠️ UYARI: Bu PDF'de finansal tutar uyuşmazlığı var!\n\n` +
        `${upload.validation_message || 'Çalışan tutarları Distributie ile uyuşmuyor.'}\n\n` +
        `Yine de göndermek istiyor musunuz?`
      );
      
      if (!confirmSend) {
        return; // Kullanıcı iptal etti
      }
    }
    
    try {
      // First get the upload data
      const uploadResponse = await axios.get(`${API}/uploads/${id}`);
      const uploadData = uploadResponse.data;
      
      // Send directly to external API from frontend
      const response = await fetch('https://www.crmportal.be/api/invoices/ingest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(uploadData.raw_json)
      });
      
      const result = await response.json();
      
      if (response.ok && (result.success || result.status === 'ok')) {
        // Update status in our database
        await axios.post(`${API}/send-to-server/${id}`);
        toast.success("Sunucuya başarıyla gönderildi!");
        fetchUploads();
      } else {
        toast.error(result.message || "Sunucu hatası");
      }
    } catch (error) {
      console.error("Error sending to server:", error);
      toast.error("Sunucuya gönderim hatası: " + (error.message || ""));
    }
  };

  const handleBulkSend = async () => {
    // Get all pending uploads WITHOUT validation warnings
    const allPendingUploads = uploads.filter(upload => upload.sent_status === 'pending');
    const pendingUploads = allPendingUploads.filter(upload => !upload.has_validation_warning);
    const warningUploads = allPendingUploads.filter(upload => upload.has_validation_warning);
    
    if (allPendingUploads.length === 0) {
      toast.info("Gönderilecek bekleyen kayıt yok");
      return;
    }
    
    if (pendingUploads.length === 0) {
      toast.error("Tüm bekleyen PDF'lerde tutar uyuşmazlığı var. Önce sorunları düzeltmelisiniz.");
      return;
    }

    let confirmMessage = `${pendingUploads.length} adet PDF sunucuya gönderilecek.`;
    if (warningUploads.length > 0) {
      confirmMessage += `\n\n⚠️ ${warningUploads.length} adet uyarılı PDF atlanacak (tutar uyuşmazlığı nedeniyle).`;
    }
    confirmMessage += `\n\nDevam etmek istiyor musunuz?`;
    
    const confirmed = window.confirm(confirmMessage);
    
    if (!confirmed) return;

    setBulkSending(true);
    toast.info(`${pendingUploads.length} PDF gönderiliyor...`);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < pendingUploads.length; i++) {
      const upload = pendingUploads[i];
      
      try {
        // Get upload data
        const uploadResponse = await axios.get(`${API}/uploads/${upload.id}`);
        const uploadData = uploadResponse.data;
        
        // Send to external API
        const response = await fetch('https://www.crmportal.be/api/invoices/ingest', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(uploadData.raw_json)
        });
        
        const result = await response.json();
        
        if (response.ok && (result.success || result.status === 'ok')) {
          // Update status in our database
          await axios.post(`${API}/send-to-server/${upload.id}`);
          successCount++;
          toast.success(`✅ ${upload.filename} gönderildi (${i + 1}/${pendingUploads.length})`);
        } else {
          errorCount++;
          toast.error(`❌ ${upload.filename} gönderilemedi`);
        }
      } catch (error) {
        console.error(`Error sending ${upload.filename}:`, error);
        errorCount++;
        toast.error(`❌ ${upload.filename} gönderilemedi`);
      }
    }
    
    setBulkSending(false);
    
    // Show summary
    if (successCount > 0 && errorCount === 0) {
      toast.success(`🎉 Tüm PDF'ler başarıyla gönderildi! (${successCount}/${pendingUploads.length})`);
    } else if (successCount > 0) {
      toast.info(`✅ ${successCount} başarılı, ❌ ${errorCount} hata`);
    } else {
      toast.error(`❌ Hiçbir PDF gönderilemedi`);
    }
    
    // Refresh list
    fetchUploads();
  };

  const handleBulkDelete = async () => {
    if (uploads.length === 0) {
      toast.info("Silinecek kayıt yok");
      return;
    }

    // İlk onay
    const firstConfirm = window.confirm(
      `⚠️ TOPLU SİLME\n\n` +
      `Bu sayfadaki ${uploads.length} adet PDF tamamen silinecek!\n\n` +
      `Devam etmek istiyor musunuz?`
    );
    
    if (!firstConfirm) return;

    // İkinci onay (son uyarı)
    const secondConfirm = window.confirm(
      `🚨 SON UYARI!\n\n` +
      `${uploads.length} adet PDF kalıcı olarak silinecek!\n\n` +
      `Bu işlem GERİ ALINAMAZ!\n\n` +
      `Emin misiniz?`
    );
    
    if (!secondConfirm) return;

    try {
      const uploadIds = uploads.map(u => u.id);
      
      toast.info(`${uploads.length} PDF siliniyor...`);
      
      const response = await axios.post(`${API}/uploads/bulk-delete`, {
        upload_ids: uploadIds
      });
      
      toast.success(`✅ ${response.data.deleted_count} PDF başarıyla silindi!`);
      
      // Refresh uploads list (automatically loads next page if available)
      fetchUploads();
      
    } catch (error) {
      console.error("Toplu silme hatası:", error);
      toast.error("Silme işlemi başarısız: " + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-4 relative">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-800" data-testid="dashboard-title">
              PDF Veri Çıkarma Paneli
            </h1>
            {/* Settings Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/settings")}
              className="text-gray-600 hover:text-blue-600 hover:border-blue-600"
              title="Ayarlar"
            >
              <Settings className="w-4 h-4" />
            </Button>
            {/* Logout Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem("hyk_auth");
                navigate("/login");
              }}
              className="text-gray-600 hover:text-red-600 hover:border-red-600"
            >
              Çıkış Yap
            </Button>
          </div>
          <p className="text-lg text-gray-600" data-testid="dashboard-subtitle">
            Fatura PDF'lerini yükleyin ve verileri otomatik olarak çıkarın
          </p>
        </div>

        {/* Upload Area */}
        <Card className="glass-card hover-lift" data-testid="upload-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="upload-title">
              <Upload className="w-6 h-6" />
              PDF Yükle
            </CardTitle>
            <CardDescription data-testid="upload-description">
              PDF dosyanızı sürükleyip bırakın veya tıklayarak seçin
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Duplicate Mode Selector */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="duplicate-mode-selector">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Aynı Fatura Numarası:
                </span>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setDuplicateMode("ask")}
                    data-testid="duplicate-mode-ask"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      duplicateMode === "ask"
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    ❓ Sor
                  </button>
                  <button
                    type="button"
                    onClick={() => setDuplicateMode("add")}
                    data-testid="duplicate-mode-add"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      duplicateMode === "add"
                        ? "bg-green-600 text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    ✅ Ekle
                  </button>
                  <button
                    type="button"
                    onClick={() => setDuplicateMode("skip")}
                    data-testid="duplicate-mode-skip"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      duplicateMode === "skip"
                        ? "bg-orange-600 text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    ⏭️ Atla
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {duplicateMode === "ask" && "Aynı fatura varsa onay istenir"}
                {duplicateMode === "add" && <span className="text-green-600 font-medium">Aynı fatura olsa bile sormadan ekler</span>}
                {duplicateMode === "skip" && <span className="text-orange-600 font-medium">Aynı fatura varsa sormadan atlar</span>}
              </div>
            </div>
            
            <div
              data-testid="drop-zone"
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                dragActive
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                data-testid="file-input"
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => e.target.files.length > 0 && handleFileUpload(e.target.files)}
                className="hidden"
                id="file-upload"
                disabled={uploading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
                data-testid="file-upload-label"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <FileText className="w-10 h-10 text-white" />
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-700" data-testid="upload-instruction">
                    {uploading ? "Yükleniyor..." : "PDF dosyası seçin veya buraya bırakın"}
                  </p>
                  <p className="text-sm text-gray-500 mt-2" data-testid="upload-hint">
                    Desteklenen format: PDF
                  </p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Upload Progress */}
        {uploadProgress.length > 0 && (
          <Card className="glass-card" data-testid="upload-progress-card">
            <CardHeader>
              <CardTitle>Yükleniyor... ({uploadProgress.filter(p => p.status === 'completed').length}/{uploadProgress.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {uploadProgress.map((progress) => (
                  <div key={progress.id} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{progress.filename}</span>
                        <span className="text-sm text-gray-500">
                          {progress.status === 'uploading' && '⏳ Yükleniyor...'}
                          {progress.status === 'completed' && '✅ Tamamlandı'}
                          {progress.status === 'failed' && '❌ Hata'}
                          {progress.status === 'skipped' && '⏭️ Atlandı'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            progress.status === 'completed' ? 'bg-green-500' : 
                            progress.status === 'failed' ? 'bg-red-500' : 
                            progress.status === 'skipped' ? 'bg-orange-500' :
                            'bg-blue-500 animate-pulse'
                          }`}
                          style={{width: progress.status === 'skipped' ? '100%' : `${progress.progress}%`}}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search & Filter */}
        <Card className="glass-card" data-testid="search-card">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              {/* Search and Company Filter Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    data-testid="search-input"
                    type="text"
                    placeholder="Dosya adı, fatura numarası veya şirket adı ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 text-base"
                  />
                </div>
                <select
                  value={selectedCompany}
                  onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="h-12 px-4 rounded-md border border-gray-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="company-filter"
                >
                  <option value="">Tüm Şirketler</option>
                  {companies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Prefactuurdatum and Week Filter Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Prefactuurdatum
                  </label>
                  <select
                    value={selectedInvoiceDate}
                    onChange={(e) => {
                      setSelectedInvoiceDate(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="w-full h-10 px-4 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="invoice-date-filter"
                  >
                    <option value="">Tümü</option>
                    {invoiceDates.map((date) => (
                      <option key={date} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Hafta
                  </label>
                  <select
                    value={selectedWeekNumber}
                    onChange={(e) => {
                      setSelectedWeekNumber(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="w-full h-10 px-4 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="week-filter"
                  >
                    <option value="">Tümü</option>
                    {weekNumbers.map((week) => (
                      <option key={week} value={week}>
                        Hafta {week}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Warning and Zero Fixed Filter Row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertTriangle className="w-4 h-4 inline mr-1 text-red-500" />
                    Tutar Uyuşmazlığı
                  </label>
                  <select
                    value={warningFilter}
                    onChange={(e) => {
                      setWarningFilter(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="w-full h-10 px-4 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="warning-filter"
                  >
                    <option value="">Tümü</option>
                    <option value="true">⚠️ Uyarılı Olanlar</option>
                    <option value="false">✅ Sorunsuz Olanlar</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertTriangle className="w-4 h-4 inline mr-1 text-orange-500" />
                    Vast €0
                  </label>
                  <select
                    value={zeroFixedFilter}
                    onChange={(e) => {
                      setZeroFixedFilter(e.target.value);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="w-full h-10 px-4 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="zero-fixed-filter"
                  >
                    <option value="">Tümü</option>
                    <option value="true">⚠️ Vast €0 Olanlar</option>
                    <option value="false">✅ Vast Dolu Olanlar</option>
                  </select>
                </div>
                {/* Clear all filters button */}
                {(selectedCompany || selectedInvoiceDate || selectedWeekNumber || dateFilter || searchTerm || warningFilter || zeroFixedFilter) && (
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSelectedCompany("");
                        setSelectedInvoiceDate("");
                        setSelectedWeekNumber("");
                        setDateFilter("");
                        setSearchTerm("");
                        setStartDate("");
                        setEndDate("");
                        setWarningFilter("");
                        setZeroFixedFilter("");
                        setPagination(prev => ({ ...prev, page: 1 }));
                        toast.success("Tüm filtreler temizlendi");
                      }}
                      className="h-10 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                )}
              </div>
              
              {/* Date Filter Row */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">Tarih Filtresi:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={dateFilter === '' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setDateFilter('');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    Tümü
                  </Button>
                  <Button
                    variant={dateFilter === 'today' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setDateFilter('today');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    Bugün
                  </Button>
                  <Button
                    variant={dateFilter === 'yesterday' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setDateFilter('yesterday');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    Dün
                  </Button>
                  <Button
                    variant={dateFilter === 'last7days' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setDateFilter('last7days');
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                  >
                    Son 7 Gün
                  </Button>
                  <Button
                    variant={dateFilter === 'custom' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateFilter('custom')}
                  >
                    Tarih Aralığı
                  </Button>
                </div>
              </div>
              
              {/* Custom Date Range Inputs */}
              {dateFilter === 'custom' && (
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Başlangıç Tarihi
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-10"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bitiş Tarihi
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-10"
                    />
                  </div>
                  <Button
                    onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                    disabled={!startDate || !endDate}
                    className="h-10"
                  >
                    Filtrele
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Uploads List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-2xl font-bold text-gray-800" data-testid="uploads-title">
              Yüklemeler ({pagination.total})
            </h2>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* Toplu Sil Butonu */}
              {uploads.length > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-lg"
                  data-testid="bulk-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Toplu Sil ({uploads.length})
                </Button>
              )}
              
              {/* Toplu Gönder Butonu */}
              {(() => {
                const pendingCount = uploads.filter(u => u.sent_status === 'pending' && !u.has_validation_warning).length;
                const warningCount = uploads.filter(u => u.sent_status === 'pending' && u.has_validation_warning).length;
                
                if (pendingCount === 0 && warningCount === 0) return null;
                
                return (
                  <div className="flex items-center gap-3">
                    {warningCount > 0 && (
                      <span className="text-sm text-gray-600">
                        <AlertTriangle className="w-4 h-4 inline mr-1 text-orange-500" />
                        {warningCount} uyarılı PDF atlanacak
                      </span>
                    )}
                    <Button
                      onClick={handleBulkSend}
                      disabled={bulkSending || pendingCount === 0}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="bulk-send-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {bulkSending ? "Gönderiliyor..." : `Toplu Gönder (${pendingCount})`}
                    </Button>
                  </div>
                );
              })()}
            </div>
          </div>
          
          {uploads.length === 0 ? (
            <Card className="glass-card" data-testid="no-uploads-card">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg" data-testid="no-uploads-message">
                  Henüz yükleme yok. Başlamak için bir PDF yükleyin.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="uploads-list">
              {uploads.map((upload) => (
                <Card
                  key={upload.id}
                  data-testid={`upload-card-${upload.id}`}
                  className="glass-card hover-lift cursor-pointer transition-all"
                  onClick={() => navigate(`/upload/${upload.id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg text-gray-800" data-testid={`upload-filename-${upload.id}`}>
                              {upload.company_name || upload.filename}
                            </h3>
                            {upload.total_amount && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm font-semibold">
                                {upload.total_amount}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{upload.filename}</p>
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                            {upload.invoice_number && (
                              <span className="flex items-center gap-1" data-testid={`upload-invoice-${upload.id}`}>
                                <FileText className="w-4 h-4" />
                                {upload.invoice_number}
                              </span>
                            )}
                            <span className="flex items-center gap-1" data-testid={`upload-date-${upload.id}`}>
                              <Calendar className="w-4 h-4" />
                              {formatDate(upload.upload_date)}
                            </span>
                            {/* Durum */}
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              upload.sent_status === 'sent' ? 'bg-green-100 text-green-700' :
                              upload.sent_status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`} data-testid={`upload-status-${upload.id}`}>
                              {upload.sent_status === 'sent' ? '✓ Gönderildi' :
                               upload.sent_status === 'failed' ? '✗ Hata' :
                               '⏳ Beklemede'}
                            </span>
                            
                            {/* Validation Warning Badge */}
                            {upload.has_validation_warning && (
                              <span 
                                className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300 flex items-center gap-1"
                                data-testid={`validation-warning-${upload.id}`}
                                title={upload.validation_message || "Tutar uyuşmazlığı var"}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Uyarı
                              </span>
                            )}
                            
                            {/* Zero Fixed Charge Warning Badge */}
                            {upload.has_zero_fixed_charge && (
                              <span 
                                className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-300 flex items-center gap-1"
                                data-testid={`zero-fixed-warning-${upload.id}`}
                                title="Sabit Ücret (Vast) €0,00"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Vast 0
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 flex-wrap">
                        {/* PDF Ön İzleme Butonu */}
                        <Button
                          data-testid={`preview-button-${upload.id}`}
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${API}/uploads/${upload.id}/download`, '_blank');
                          }}
                          title="PDF'i yeni sekmede aç"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Ön İzleme
                        </Button>
                        
                        {/* PDF İndirme Butonu */}
                        <Button
                          data-testid={`download-button-${upload.id}`}
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement('a');
                            link.href = `${API}/uploads/${upload.id}/download`;
                            link.download = upload.filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          title="PDF'i indir"
                        >
                          <FileDown className="w-4 h-4 mr-1" />
                          İndir
                        </Button>
                        
                        {upload.sent_status !== 'sent' && (
                          <Button
                            data-testid={`send-button-${upload.id}`}
                            variant="default"
                            size="sm"
                            onClick={(e) => handleSendToServer(upload.id, e)}
                            className={upload.has_validation_warning 
                              ? "bg-orange-600 hover:bg-orange-700" 
                              : "bg-green-600 hover:bg-green-700"}
                            title={upload.has_validation_warning 
                              ? "⚠️ Tutar uyuşmazlığı var - Tıklayınca onay istenecek" 
                              : "Sunucuya gönder"}
                          >
                            <Building2 className="w-4 h-4 mr-1" />
                            Gönder
                          </Button>
                        )}
                        <Button
                          data-testid={`view-button-${upload.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/upload/${upload.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Görüntüle
                        </Button>
                        <Button
                          data-testid={`delete-button-${upload.id}`}
                          variant="destructive"
                          size="sm"
                          onClick={(e) => handleDelete(upload.id, e)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Pagination Controls */}
          {pagination.total > 0 && (
            <Card className="glass-card mt-6">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Page Info */}
                  <div className="text-sm text-gray-600">
                    Toplam {pagination.total} kayıt - Sayfa {pagination.page} / {pagination.total_pages}
                    {uploads.length > 0 && (
                      <span className="ml-2">
                        ({((pagination.page - 1) * pagination.limit) + 1} - {((pagination.page - 1) * pagination.limit) + uploads.length} arası gösteriliyor)
                      </span>
                    )}
                  </div>
                  
                  {/* Page Navigation */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
                      disabled={!pagination.has_prev}
                    >
                      İlk
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={!pagination.has_prev}
                    >
                      Önceki
                    </Button>
                    
                    {/* Page Numbers */}
                    <div className="flex gap-1">
                      {[...Array(Math.min(5, pagination.total_pages))].map((_, idx) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = idx + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = idx + 1;
                        } else if (pagination.page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + idx;
                        } else {
                          pageNum = pagination.page - 2 + idx;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className="w-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={!pagination.has_next}
                    >
                      Sonraki
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: pagination.total_pages }))}
                      disabled={!pagination.has_next}
                    >
                      Son
                    </Button>
                  </div>
                  
                  {/* Items per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Sayfa başına:</span>
                    <select
                      value={pagination.limit}
                      onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                      className="h-8 px-2 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
